import React, { useCallback } from "react";
import { Spinner, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { format, addDays, eachDayOfInterval, parseISO } from "date-fns";
import { Collapse } from "react-collapse";

import ServicePicker from "../../../Common/Components/ServicePicker";
import LaundryItemCard from "../../../Common/Components/LaundryItemCard";
import { ItemCounter } from "../../../Common/Components/ItemCounter";
import { DatePicker } from "../../../Common/Components/DatePicker";
import OrderNotesPicker from "../../../Common/Components/OrderNotesPicker";
import { TitleHeader } from "../../../Common/Components/TitleHeader";
import useCartTotal from "../../../Common/Hooks/useCartTotal";
import useTax from "../../../Common/Hooks/useTax";
import { isDateBetween } from "../../../Services/MainService";
import {
  calculateBundleOffer,
  getBundleMessage,
} from "../../../Services/CartService";
import {
  fetchLaundryItem,
  fetchServices,
  fetchTimeSlot,
  fetchCoupon,
  addOrder as storeOrderCreate,
} from "../../Services/OrderService";
import { fetchWeekDaysOff } from "../../../Services/OrderService";
import { fetchCustomers, fetchCustomerDetails } from "../../Services/CustomerService";
import { AddressPicker } from "../../Components/AddressPicker";
import { BsSearch, BsXCircle } from "react-icons/bs";
import {
  buildSameDaySlotOptions,
  getSameDayErrorMessage,
  isSameDayDeliveryType,
} from "./sameDay";
// Seal functionality disabled:
// import { LuScanLine } from "react-icons/lu";
// import { CiSquareMinus } from "react-icons/ci";
// import { BsPlusSquare } from "react-icons/bs";
import "./NewOrder.css";

const stepLabels = [
  "Customer",
  "Order Type",
  "Add Items",
  "Summary",
  "Review & Place",
];

export const NewOrder = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const config = useSelector((x) => x.Config);
  const store = useSelector((x) => x.Store);
  const dts = useSelector((x) => x.OrderTypes);
  const orderTypeId = useSelector((x) => x.OrderTypeId);
  const bookingType = useSelector((x) => x.BookingType);
  const cart = useSelector((x) => x.Cart);
  const lastService = useSelector((x) => x.Service);
  const lastCategory = useSelector((x) => x.Category);
  const customer = useSelector((x) => x.Customer);
  const customerId = useSelector((x) => x.CustomerId);
  const processing = useSelector((x) => x.Processing);
  const dates = useSelector((x) => x.Dates);
  const addresses = useSelector((x) => x.Addresses);
  const selfPick = useSelector((x) => x.SelfPick);
  const coupon = useSelector((x) => x.Coupon);
  const note = useSelector((x) => x.Note);
  const apiNotes = useSelector((x) => x.Customer?.customer_note) || [];

  // --- Step state ---
  const [step, setStep] = React.useState(1);
  const [selectedCustomer, setSelectedCustomer] = React.useState(null);

  // --- Customer search ---
  const [searchTerm, setSearchTerm] = React.useState("");
  const [customerResults, setCustomerResults] = React.useState([]);
  const [customerLoading, setCustomerLoading] = React.useState(false);
  const searchTimer = React.useRef(null);

  // --- Services / Items ---
  const [services, setServices] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [initLoading, setInitLoading] = React.useState(true);
  const [itemsLoading, setItemsLoading] = React.useState(false);
  const [laundryItems, setLaundryItem] = React.useState([]);
  const [campaign, setCampaign] = React.useState(null);
  const [payload, setPayload] = React.useState({
    serviceId: null,
    categoryId: null,
    limit: 100,
    offset: 1,
  });
  const [reload, setReload] = React.useState("");
  const [fetchError, setFetchError] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [open, setOpen] = React.useState({});
  const [cShow, setCShow] = React.useState(false);
  const [tempServ, setTempServ] = React.useState(null);

  // --- Summary state ---
  const [deliveryTypes, setDeliveryTypes] = React.useState(null);
  const [weekDaysOff, setWeekDaysOff] = React.useState(null);
  const [pickupRange, setPickupRange] = React.useState([]);
  const [deliveryRange, setDeliveryRange] = React.useState([]);
  const [showDatePicker, setShowDatePicker] = React.useState(null);
  const [date, setDate] = React.useState({
    pickup: { date: null, slot: null },
    delivery: { date: null, slot: null },
  });
  const [sameDayToken, setSameDayToken] = React.useState(null);
  const [address, setAddress] = React.useState({});
  const [loaded, setLoaded] = React.useState(false);
  const [datesLoading, setDatesLoading] = React.useState(false);
  const [code, setCode] = React.useState("");
  const [cLoading, setCLoading] = React.useState(false);
  const [addressModal, setAddressModal] = React.useState(null);

  const selectedDeliveryType = deliveryTypes?.[orderTypeId];
  const isSameDay = isSameDayDeliveryType(selectedDeliveryType);
  const sameDayDeliveryTypeId = isSameDay ? Number(orderTypeId) : null;

  // --- Review state ---
  const [submitting, setSubmitting] = React.useState(false);
  const orderSubmissionRef = React.useRef(null);
  const [paymentMethod, setPaymentMethod] = React.useState("cash_on_delivery");
  // Seal functionality disabled:
  // const [sealNo, setSealNo] = React.useState("");
  // const [childSeal, setChildSeal] = React.useState([]);

  // --- Cleanup on mount ---
  React.useEffect(() => {
    dispatch({ type: "setCart", payload: [] });
    dispatch({ type: "setDates", payload: null });
    dispatch({ type: "setOrderType", payload: null });
    dispatch({ type: "setOrderTypeId", payload: null });
    dispatch({ type: "setBookingType", payload: null });
    dispatch({ type: "setCustomerId", payload: null });
    dispatch({ type: "setSelfPick", payload: null });
    dispatch({ type: "setCoupon", payload: null });
    dispatch({ type: "setNote", payload: null });
    dispatch({ type: "setService", payload: null });
    dispatch({ type: "setCategory", payload: null });
    dispatch({ type: "setPickupDate", payload: null });
    dispatch({ type: "setDeliveryDate", payload: null });
    dispatch({ type: "setAddresses", payload: null });
    dispatch({ type: "setZone", payload: null });
  }, [dispatch]);

  React.useEffect(() => {
    setSameDayToken(null);
  }, [orderTypeId]);

  // ==================== CUSTOMER SEARCH ====================
  const doCustomerSearch = useCallback((term) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      if (!term.trim()) {
        setCustomerResults([]);
        return;
      }
      setCustomerLoading(true);
      const t = term.trim();
      const isPhone = /^[\d+]+$/.test(t);
      const params = isPhone
        ? `?phone=${encodeURIComponent("+92" + t.replace(/^\+?/, ""))}`
        : `?name=${encodeURIComponent(t)}`;
      fetchCustomers(undefined, params).then(
        ({ data }) => {
          setCustomerResults(data?.data || []);
          setCustomerLoading(false);
        },
        () => {
          setCustomerLoading(false);
          toast.error("Customer search failed.");
        }
      );
    }, 400);
  }, []);

  const selectCustomer = (c) => {
    setSelectedCustomer(c);
    dispatch({ type: "setCustomer", payload: c });
    dispatch({ type: "setCustomerId", payload: c.id });
    setCustomerResults([]);
    setSearchTerm("");
    fetchCustomerDetails(c.id).then(
      (res) => {
        const full = res.data?.[0];
        if (full) dispatch({ type: "setCustomer", payload: full });
      },
      () => {}
    );
    setStep(2);
  };

  const resetCustomer = () => {
    setSelectedCustomer(null);
    dispatch({ type: "setCustomer", payload: null });
    dispatch({ type: "setCustomerId", payload: null });
    setStep(1);
  };

  // ==================== ORDER TYPE ====================
  const [bookingRadio, setBookingRadio] = React.useState(false);
  const [showBespokeTypes, setShowBespokeTypes] = React.useState(false);

  const selectProcessing = (val) => {
    dispatch({ type: "setProcessing", payload: val });
  };

  const selectBooking = (typeId, title) => {
    console.log(`[DEBUG] selectBooking → typeId:${typeId}, title:"${title}"`);
    if (typeId == 1 || typeId == 2) {
      console.log(`[DEBUG] selectBooking → IF branch → bookingType=${typeId}`);
      dispatch({ type: "setBookingType", payload: typeId });
      dispatch({ type: "setOrderTypeId", payload: typeId });
      dispatch({ type: "setOrderType", payload: title.toLowerCase().trim() });
      setStep(3);
    } else {
      console.log(`[DEBUG] selectBooking → ELSE branch → bookingType=1 (hardcoded)`);
      dispatch({ type: "setOrderTypeId", payload: typeId });
      dispatch({ type: "setOrderType", payload: title.toLowerCase().trim() });
      dispatch({ type: "setBookingType", payload: 1 });
      dispatch({ type: "setProcessing", payload: "hub" });
      setBookingRadio(true);
    }
  };

  const selectBookingType = (id) => {
    console.log(`[DEBUG] selectBookingType → id:${id}`);
    dispatch({ type: "setBookingType", payload: id });
    setStep(3);
  };

  const alreadySelectedProcessing = useSelector((x) => x.Processing);
  const alreadySelectedOrderType = useSelector((x) => x.OrderType);

  // ==================== ITEMS (Step 3) ====================
  const formik = useFormik({
    initialValues: { cart: { items: [] } },
  });

  React.useEffect(() => {
    if (step >= 3) {
      setInitLoading(true);
      fetchServices(100, 1, orderTypeId)
        .then((res) =>
          res.filter((x) => {
            if (orderTypeId == 1 || orderTypeId == 2) {
              if (!x.delivery_types) return true;
              else if (x.delivery_types.length == 0) return true;
              else return x.delivery_types.some((y) => y.id == orderTypeId);
            } else if (isSameDay && Array.isArray(x.delivery_types)) {
              return x.delivery_types.some((y) => y.id == orderTypeId);
            } else return true;
          })
        )
        .then((res) => {
          setServices(res);
          const selectedService =
            lastService && lastService !== "null"
              ? res.find((x) => x.id === parseInt(lastService))
              : res[0];
          const cats = selectedService?.categories || [];
          setCategories(cats);
          const selectedCategory =
            lastCategory && lastCategory !== "null"
              ? cats.find((x) => x.id === parseInt(lastCategory))
              : cats[0];
          setPayload((s) => ({
            ...s,
            serviceId: selectedService?.id,
            categoryId: selectedCategory?.id,
          }));
          setInitLoading(false);
        });
    }
  }, [step, orderTypeId, isSameDay]);

  React.useEffect(() => {
    if (!initLoading && step >= 3) {
      const controller = new AbortController();
      setItemsLoading(true);
      setLaundryItem([]);
      setFetchError(false);
      fetchLaundryItem(payload, controller.signal).then(
        (res) => {
          if (controller.signal.aborted) return;
          setItemsLoading(false);
          setLaundryItem(res.data);
          setCampaign(res.campain?.[0]);
        },
        (er) => {
          if (controller.signal.aborted) return;
          setItemsLoading(false);
          setFetchError(true);
        }
      );
      return () => controller.abort();
    }
  }, [initLoading, payload, reload, step]);

  React.useEffect(() => {
    if (step >= 3 && !initLoading) {
      formik.setValues((y) => ({
        ...y,
        cart: {
          ...y.cart,
          items: laundryItems.map((x) => {
            const fCart = cart.find(
              (f) =>
                f.laundry_item_id === x.id && f.serviceId === x.services[0]?.id
            );
            const newPrice =
              bookingType == 2
                ? typeof x.services[0].pivot.e_price == "string"
                  ? parseFloat(x.services[0].pivot.e_price)
                  : x.services[0].pivot.e_price
                : typeof x.services[0].pivot.price == "string"
                  ? parseFloat(x.services[0].pivot.price)
                  : x.services[0].pivot.price;
            if (fCart) {
              const fb = {
                ...fCart,
                addons: x.addons.map((z) => {
                  const fAd = fCart.addons.find((ffAd) => ffAd.id === z.id);
                  return fAd || { id: z.id, name: z.name, price: z.price, qty: 0 };
                }),
                price: newPrice,
                actualPrice: newPrice,
              };
              if (campaign) fb.price -= fb.price * (campaign.discount / 100);
              return fb;
            } else {
              const fb = {
                laundry_item_id: x.id,
                laundry_item_category_id: parseInt(x.laundry_item_category_id),
                price: newPrice,
                actualPrice: newPrice,
                quantity: 0,
                name: x.name,
                serviceId: x.services[0]?.id,
                is_bundle_active: false,
                bundle_buy: x.bundle_offer?.required_items,
                bundle_free: x.bundle_offer?.total_discount_items,
                free_items: 0,
                addons: x.addons.map((z) => ({
                  id: z.id,
                  name: z.name,
                  price: z.price,
                  qty: 0,
                })),
              };
              if (campaign) fb.price -= fb.price * (campaign.discount / 100);
              return fb;
            }
          }),
        },
      }));
    }
  }, [laundryItems, bookingType, campaign, step, initLoading]);

  React.useEffect(() => {
    if (!initLoading && step >= 3) {
      const items = [...formik.values.cart.items];
      const currentCart = [...cart];
      let updatedCart = currentCart.map((item) => {
        const n = items.find(
          (ni) =>
            ni.laundry_item_id === item.laundry_item_id &&
            ni.serviceId === item.serviceId
        );
        return n || item;
      });
      items
        .filter((x) => x.quantity !== 0)
        .forEach((ni) => {
          if (
            !updatedCart.find(
              (x) =>
                x.laundry_item_id === ni.laundry_item_id &&
                x.serviceId === ni.serviceId
            )
          )
            updatedCart.push(ni);
        });
      updatedCart = updatedCart.map((x) => ({
        ...x,
        addons: x.addons.filter((z) => z.qty !== 0),
      }));
      updatedCart = updatedCart.filter((x) => x.quantity !== 0);
      dispatch({ type: "setCart", payload: updatedCart });
    }
  }, [formik.values, initLoading, dispatch, step]);

  const selectService = (serviceId, clearTemp) => {
    setSearch("");
    if (
      !isSameDay &&
      orderTypeId != 1 && 
      orderTypeId != 2 && 
      serviceId != lastService && 
      total != 0 && 
      !clearTemp
    ) {
      setTempServ(serviceId);
    } else {
      setTempServ(null);
      const serv = services.find((x) => x.id === serviceId);
      const cats = serv?.categories || [];
      setCategories(cats);
      setPayload((s) => ({ ...s, serviceId, categoryId: cats[0]?.id }));
      dispatch({ type: "setService", payload: serviceId });
    }
  };

  const cCart = (serv, isServ = true) => {
    dispatch({ type: "setCart", payload: [] });
    formik.setValues({ cart: { items: [] } });
    if (isServ && serv) {
      setTimeout(() => { selectService(serv, true); }, 100);
    } else {
      dispatch({ type: "setService", payload: null });
      dispatch({ type: "setCategory", payload: null });
      dispatch({ type: "setPickupDate", payload: null });
      dispatch({ type: "setDeliveryDate", payload: null });
    dispatch({ type: "setAddresses", payload: null });
    dispatch({ type: "setZone", payload: null });
      setReload(Date.now().toString());
    }
  };

  const selectCategory = (categoryId) => {
    setSearch("");
    setPayload((s) => ({ ...s, categoryId }));
    dispatch({ type: "setCategory", payload: categoryId });
  };

  const changeItemQuantity = (index, value) => {
    formik.setValues((v) => ({
      cart: {
        items: v.cart.items.map((vi, i) => {
          if (i === index) {
            const item = { ...vi, quantity: value };
            if (item.bundle_buy > 0)
              item.free_items = calculateBundleOffer({
                totalQuantity: value,
                bundleBuy: item.bundle_buy,
                bundleFree: item.bundle_free,
                itemPrice: item.price,
              }).freeItems;
            item.is_bundle_active = item.free_items > 0;
            return item;
          }
          return vi;
        }),
      },
    }));
  };

  const changeAddonQuantity = (itemIndex, addonIndex, qty) => {
    formik.setValues((v) => ({
      cart: {
        items: v.cart.items.map((vi, i) =>
          i === itemIndex
            ? {
                ...vi,
                addons: vi.addons.map((ad, ai) =>
                  ai === addonIndex ? { ...ad, qty } : ad
                ),
              }
            : vi
        ),
      },
    }));
  };

  const toggleAddons = (index) => {
    setLaundryItem((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, showAddons: !item.showAddons } : item
      )
    );
  };

  const getLaundryItem = (id) => laundryItems.find((x) => x.id === id);
  const getOffer = (id) => getLaundryItem(id)?.bundle_offer;

  const [amount, total] = useCartTotal();

  const filteredItems = formik.values.cart.items
    .map((item, i) => ({ item, i }))
    .filter(({ item }) => {
      if (!search) return true;
      const li = getLaundryItem(item.laundry_item_id);
      const name = (li?.name || "").toLowerCase();
      const desc = (li?.description || "").toLowerCase();
      const q = search.toLowerCase();
      return name.includes(q) || desc.includes(q);
    });

  const changeItemNote = (itemIndex, item_note) => {
    formik.setValues((v) => ({
      cart: {
        items: v.cart.items.map((item, i) =>
          i === itemIndex ? { ...item, item_note } : item
        ),
      },
    }));
  };

  // Group cart by service for accordion preview
  const groupedCart = React.useMemo(() => {
    if (cart.length === 0 || services.length === 0) return [];
    const groups = {};
    cart.forEach((item) => {
      const sid = item.serviceId;
      if (!groups[sid]) {
        groups[sid] = {
          services_id: sid,
          service: services.find((s) => s.id === sid)?.name || `Service ${sid}`,
          items: [],
        };
      }
      groups[sid].items.push(item);
    });
    return Object.values(groups);
  }, [cart, services]);

  const timeToMinutes = (hhmmss) => {
    if (!hhmmss || typeof hhmmss !== "string") return null;
    const [h, m] = hhmmss.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  const isCutoffPassed = (cutoff_time) => {
    const cutoffMin = timeToMinutes(cutoff_time);
    if (cutoffMin === null) return false;
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    return nowMin >= cutoffMin;
  };

  // ==================== SUMMARY (Step 4) ====================
  React.useEffect(() => {
    if (step >= 4 && (selfPick === null || selfPick === undefined)) {
      dispatch({ type: "setSelfPick", payload: "false" });
    }
  }, [step, selfPick, dispatch]);

  const FilterRange = useCallback(
    (range) => {
      if (!weekDaysOff) return range;
      return range.filter((d) => {
        const day = format(d, "EEEE").toLowerCase();
        const ds = format(d, "yyyy-MM-dd");
        return (
          !weekDaysOff.weekdaysOff.includes(day) &&
          !weekDaysOff.offDays.includes(ds)
        );
      });
    },
    [weekDaysOff]
  );

  React.useEffect(() => {
    if (step >= 3 && orderTypeId) {
      const r = dts.reduce((result, item) => {
        result[item.id] = item;
        return result;
      }, {});
      setDeliveryTypes(r);
      fetchWeekDaysOff(orderTypeId).then(
        (res) => setWeekDaysOff(res),
        () => toast.error("Something went wrong!")
      );
    }
  }, [step, orderTypeId, dts]);

  React.useEffect(() => {
    if (weekDaysOff && deliveryTypes && step >= 4) {
      const sd = addDays(new Date(), deliveryTypes[orderTypeId]?.pickup_duration);
      const ed = addDays(sd, (deliveryTypes[orderTypeId]?.noOf_calender_dates || 15) - 1);
      const dif = FilterRange(eachDayOfInterval({ start: sd, end: ed }));
      if (dates?.pickup?.date && dates?.pickup?.slot) {
        setDate((p) => ({
          ...p,
          pickup: { date: dates.pickup.date, slot: dates.pickup.slot },
        }));
      }
      setPickupRange(dif);
    }
  }, [dates?.pickup, weekDaysOff, deliveryTypes, orderTypeId, step, FilterRange]);

  // Auto-preselect earliest pickup date & slot when step 4 loads
  React.useEffect(() => {
    if (step >= 4 && pickupRange.length > 0 && !date?.pickup?.date) {
      const firstDate = pickupRange[0];
      const slotOptions = buildSameDaySlotOptions({
        deliveryTypeId: sameDayDeliveryTypeId,
        type: 1,
      });
      fetchTimeSlot(
        format(firstDate, "yyyy-MM-dd"),
        1,
        bookingType,
        undefined,
        undefined,
        slotOptions.deliveryTypeId
      ).then(
        (res) => {
          if (res && res.length > 0) {
            let idx = 0;
            if (!isSameDay) {
              while (idx < res.length && isCutoffPassed(res[idx]?.cutoff_time)) {
                idx++;
              }
              if (idx >= res.length) idx = 0;
            }
            const selectedSlot = res[idx];
            if (isSameDay) setSameDayToken(selectedSlot?.same_day_token || null);
            setDate((p) => {
              const n = { ...p, pickup: { date: firstDate, slot: selectedSlot }, delivery: { date: null, slot: null } };
              dispatch({ type: "setDates", payload: n });
              return n;
            });
          }
        },
        () => {}
      );
    }
  }, [step, pickupRange, date?.pickup?.date, bookingType, dispatch, isSameDay, sameDayDeliveryTypeId]);

  React.useEffect(() => {
    if (weekDaysOff && deliveryTypes && date?.pickup?.date && step >= 4) {
      if (isSameDay) {
        const pickupDate = format(date.pickup.date, "yyyy-MM-dd");
        const pickupSlotId = date.pickup.slot?.id;
        setDeliveryRange([date.pickup.date]);

        if (!pickupSlotId) {
          setDate((p) => ({ ...p, delivery: { date: null, slot: null } }));
          return;
        }

        const slotOptions = buildSameDaySlotOptions({
          deliveryTypeId: sameDayDeliveryTypeId,
          type: 2,
          pickupSlotId,
          pickupDate,
        });

        (async () => {
          setDatesLoading(true);
          try {
            const res = await fetchTimeSlot(
              pickupDate,
              2,
              bookingType,
              slotOptions.pickupSlotId,
              slotOptions.pickupDate,
              slotOptions.deliveryTypeId
            );
            const existingSlot = dates?.delivery?.slot;
            const selectedSlot = existingSlot
              ? res.find((slot) => slot.id === existingSlot.id)
              : res[0];

            if (!selectedSlot) {
              setDate((p) => ({ ...p, delivery: { date: null, slot: null } }));
              toast.warn("No Same Day delivery slots are available.");
              return;
            }

            setDate((p) => {
              const next = {
                ...p,
                delivery: { date: date.pickup.date, slot: selectedSlot },
              };
              dispatch({ type: "setDates", payload: next });
              return next;
            });
          } catch (error) {
            toast.error("Failed to fetch Same Day delivery slots.");
          } finally {
            setDatesLoading(false);
          }
        })();
        return;
      }

      let skip = deliveryTypes[orderTypeId]?.duration;
      if (orderTypeId != 1 && orderTypeId != 2) {
        const serv = services.find((x) => x.id == cart[0]?.serviceId);
        if (serv)
          skip = bookingType == 1 ? serv.regular_duration : serv.express_duration;
      }
      const sd = addDays(date.pickup.date, skip);
      const ed = addDays(sd, (deliveryTypes[orderTypeId]?.noOf_calender_dates || 15) - 1);
      const dif = FilterRange(eachDayOfInterval({ start: sd, end: ed }));
      setDeliveryRange(dif);
      const d_d = dates?.delivery;
      if (d_d?.date) {
        const ld = typeof d_d.date === "string" ? parseISO(d_d.date) : d_d.date;
        if (!isDateBetween(ld, sd, ed)) {
          setDate((p) => ({ ...p, delivery: { date: null, slot: null } }));
        } else if (d_d.slot) {
          setDatesLoading(true);
          fetchTimeSlot(
            format(ld, "yyyy-MM-dd"), 2, bookingType,
            date.pickup.slot?.id, format(date.pickup.date, "yyyy-MM-dd")
          ).then(
            (res) => {
              const sl = res.findIndex((x) => x.id === d_d.slot.id);
              if (~sl) {
                setDate((p) => ({ ...p, delivery: { date: d_d.date, slot: d_d.slot } }));
              } else {
                setDate((p) => ({ ...p, delivery: { date: null, slot: null } }));
                toast.warn("Delivery time slot unavailable.");
              }
              setDatesLoading(false);
            },
            () => {
              toast.error("Failed to fetch time slots.");
              setDatesLoading(false);
            }
          );
        }
      } else if (!d_d || !d_d?.slot) {
        (async () => {
          setDatesLoading(true);
          let skippedOneDay = false;
          for (let i = 0; i < dif.length; i++) {
            const d = dif[i];
            try {
              const res = await fetchTimeSlot(
                format(d, "yyyy-MM-dd"), 2, bookingType,
                date.pickup.slot?.id, format(date.pickup.date, "yyyy-MM-dd")
              );
              if (!res || res.length === 0) continue;

              const cutoffPassed = isCutoffPassed(date.pickup.slot?.cutoff_time);
              let pickIndex = 0;

              if (cutoffPassed && date.pickup.slot?.id) {
                const pickupIndex = res.findIndex(
                  (s) => s.id === date.pickup.slot.id
                );
                if (pickupIndex >= 0 && res[pickupIndex + 1]) {
                  pickIndex = pickupIndex + 1;
                } else if (pickupIndex >= 0 && !res[pickupIndex + 1]) {
                  if (!skippedOneDay) { skippedOneDay = format(d, "yyyy-MM-dd"); continue; }
                  pickIndex = 0;
                }
              }
              if (!res[pickIndex]) continue;

              setDate((p) => {
                const n = { ...p, delivery: { date: d, slot: res[pickIndex] } };
                dispatch({ type: "setDates", payload: n });
                return n;
              });
              break;
            } catch (er) {
              console.warn(er);
            }
          }
          setDatesLoading(false);
        })();
      }
    }
  }, [date?.pickup, weekDaysOff, deliveryTypes, orderTypeId, bookingType, services, cart, step, FilterRange, dispatch, isSameDay, sameDayDeliveryTypeId]);

  React.useEffect(() => {
    const ad = store?.laundry_store?.store_address?.[0];
    if (selfPick === "false" || selfPick === false) {
      if (ad) {
        const a = {
          pickup_coordinates: { longitude: ad.longitude, latitude: ad.latitude },
          destination_coordinates: { longitude: ad.longitude, latitude: ad.latitude },
          pickup_address: {
            floor: null, road: null, house: null, address: ad.address,
            contact_person_name: null, contact_person_number: null,
            addressLine1: ad.address1, addressLine2: ad.address2,
            area: ad.area?.name, area_phase: ad.phase?.name,
          },
          destination_address: {
            floor: null, road: null, house: null, address: ad.address,
            contact_person_name: null, contact_person_number: null,
            addressLine1: ad.address1, addressLine2: ad.address2,
            area: ad.area?.name, area_phase: ad.phase?.name,
          },
          isStore: true,
        };
        setAddress(a);
        dispatch({ type: "setAddresses", payload: a });
      }
    } else if ((selfPick === "true" || selfPick === true) && addresses?.isStore) {
      setAddress((v) => {
        const p = { ...v };
        delete p.destination_coordinates;
        delete p.destination_address;
        dispatch({ type: "setAddresses", payload: p });
        return p;
      });
    }
  }, [store, selfPick]);

  React.useEffect(() => {
    if (!loaded && addresses) {
      setAddress(addresses);
      setLoaded(true);
    }
  }, [addresses, loaded]);

  React.useEffect(() => {
    if (dates) {
      setDate((p) => ({
        pickup: dates.pickup || p.pickup,
        delivery: dates.delivery || p.delivery,
      }));
    }
  }, [dates]);

  const HandleDateChange = (e) => {
    if (showDatePicker === "pickup") {
      setDate((p) => {
        const n = { ...p, pickup: { date: e.date, slot: e.slot }, delivery: { date: null, slot: null } };
        if (isSameDay) setSameDayToken(e.slot?.same_day_token || null);
        dispatch({ type: "setDates", payload: n });
        return n;
      });
    } else if (showDatePicker === "delivery") {
      setDate((p) => {
        const deliveryDate = isSameDay ? p.pickup.date : e.date;
        const n = { ...p, delivery: { date: deliveryDate, slot: e.slot } };
        dispatch({ type: "setDates", payload: n });
        return n;
      });
    }
    setShowDatePicker(null);
  };

  const HandleAddressChange = (e) => {
    if (addressModal === "delivery") {
      setAddress((v) => {
        const p = {
          ...v,
          destination_coordinates: { longitude: e.longitude, latitude: e.latitude },
          destination_address: {
            floor: null, road: null, house: null, address: e.address,
            contact_person_name: null, contact_person_number: null,
            addressLine1: e.address1, addressLine2: e.address2,
            area: e.area?.[0]?.name, area_phase: e.phase?.[0]?.name,
          },
          isStore: false,
        };
        dispatch({ type: "setAddresses", payload: p });
        return p;
      });
    }
    setAddressModal(null);
  };

  const checkCoupon = () => {
    if (coupon) {
      dispatch({ type: "setCoupon", payload: null });
      setCode("");
    } else if (code.trim() && !cLoading) {
      setCLoading(true);
      fetchCoupon({ code, amount, zone_id: store?.zone_id, user_id: customerId }).then(
        (res) => {
          setCLoading(false);
          dispatch({ type: "setCoupon", payload: res });
        },
        (err) => {
          setCLoading(false);
          err?.errors?.forEach((er) => toast.error(er.message));
        }
      );
    }
  };

  const referralPercent = Number(customer?.referral_reward?.reward_amount || 0);
  const referrerFirstName = customer?.referral_reward?.consumer_f_name;
  const referrerLastName = customer?.referral_reward?.consumer_l_name;
  const [subtotal, tax, discount, deliveryCharge, totalPrice, grossTotal, referralDiscount] = useTax(
    deliveryTypes?.[orderTypeId], { referralPercent }
  );

  // ==================== REVIEW (Step 5) ====================
  const consumerRefCode = customer?.referral_reward?.consumer_referral_code;
  const rawWalletBalance = customer?.customer_wallet?.balance;
  const parsedWalletBalance = Number(rawWalletBalance);
  const hasWalletBalance = !!selectedCustomer &&
    Number(customer?.id) === Number(customerId) &&
    rawWalletBalance !== null && rawWalletBalance !== undefined &&
    rawWalletBalance !== "" && Number.isFinite(parsedWalletBalance);
  const walletBalance = hasWalletBalance ? parsedWalletBalance : 0;
  const canPayWithWallet = hasWalletBalance && walletBalance >= Number(totalPrice);

  React.useEffect(() => {
    if (paymentMethod === "wallet_on_delivery" && !canPayWithWallet) {
      setPaymentMethod("cash_on_delivery");
    }
  }, [paymentMethod, canPayWithWallet]);

  const clearNotes = () => {
    sessionStorage.removeItem("note");
    localStorage.removeItem("order_notes_saved_v1");
  };

  const afterOrder = (order, payloadBookingType) => {
    orderSubmissionRef.current = null;
    setSubmitting(false);
    try {
      const logs = JSON.parse(localStorage.getItem("_bt_logs") || "[]");
      logs.push({
        order_id: order.order_id,
        booking_type: bookingType,
        payload_booking_type: payloadBookingType,
        time: new Date().toISOString(),
      });
      if (logs.length > 500) logs.splice(0, logs.length - 500);
      localStorage.setItem("_bt_logs", JSON.stringify(logs));
    } catch {}
    navigate(`/store/orders/success/${order.order_id}`, {
      replace: true,
      state: { fromOrderPlacement: true },
    });
    clearNotes();
  };

  const handlePlaceOrder = () => {
    if (cart.length === 0) return;
    if (paymentMethod === "wallet_on_delivery" && !canPayWithWallet) {
      toast.error("Wallet balance is unavailable or insufficient for this order.");
      return;
    }

    const laundryDeliveryTypeId = Number(deliveryTypes?.[orderTypeId]?.id);
    const normalizedBookingType = Number(bookingType);
    const sameDayOrder = isSameDayDeliveryType(deliveryTypes?.[orderTypeId]);

    if (![1, 2].includes(normalizedBookingType)) {
      toast.error("Please select Regular or Express before placing the order.");
      return;
    }

    if (
      [1, 2].includes(laundryDeliveryTypeId) &&
      normalizedBookingType !== laundryDeliveryTypeId
    ) {
      toast.error("Booking type does not match delivery type. Please select it again.");
      return;
    }

    if (sameDayOrder) {
      if (!sameDayToken) {
        toast.error("Please select a valid Same Day pickup slot.");
        return;
      }
      if (
        format(date.pickup.date, "yyyy-MM-dd") !==
        format(date.delivery.date, "yyyy-MM-dd")
      ) {
        toast.error("Same Day pickup and delivery must be on the same date.");
        return;
      }
    }

    setSubmitting(true);

    let formattedNote = null;
    if (Array.isArray(note) && note.length > 0) {
      formattedNote = note;
    }

    const reviewItems = groupedCart.reduce((result, group) => {
      group.items.forEach((item) => {
        const key = item.serviceId;
        if (!result[key]) {
          result[key] = {
            services_id: key,
            service: group.service,
            open: true,
            items: [],
          };
        }
        result[key].items.push({
          laundry_item_id: item.laundry_item_id,
          laundry_item_category_id: parseInt(item.laundry_item_category_id),
          price: item.price,
          quantity: item.quantity,
          bar_code: item.bar_code,
          name: item.name,
          addons: item.addons,
          free_items: item.free_items,
          bundle_free: item.bundle_free,
          bundle_buy: item.bundle_buy,
          is_bundle_active: item.is_bundle_active,
          item_note: item.item_note,
        });
      });
      return result;
    }, {});

    const payloads = [
      {
        order_process: processing,
        // Seal functionality disabled:
        // seal_no: sealNo,
        // child_seal_nos: childSeal.map((s) => s.trim()).filter(Boolean),
        type: alreadySelectedOrderType,
        is_hub_store: selfPick === "false" || selfPick === false,
        user_id: customerId,
        payment_status: "unpaid",
        delivery_date: format(date.delivery.date, "yyyy-MM-dd HH:mm:ss.SSS"),
        pickup_date: format(date.pickup.date, "yyyy-MM-dd HH:mm:ss.SSS"),
        payment_method: paymentMethod,
        isPay: paymentMethod === "up_front_cash_payment",
        platform: 4,
        consumer_referral_code: consumerRefCode,
        referral_discount: referralDiscount,
        pickup_coordinates: address?.pickup_coordinates,
        destination_coordinates: address?.destination_coordinates,
        pickup_address: address?.pickup_address,
        destination_address: address?.destination_address,
        bar_code: "",
        order_pickup_time_slot_id: date.pickup.slot?.id,
        order_type_id: laundryDeliveryTypeId,
        order_delivery_time_slot_id: date.delivery.slot?.id,
        laundry_delivery_type_id: laundryDeliveryTypeId,
        service_id: 19,
        category_id: 1,
        delivery_charge: deliveryCharge,
        pickup_schedule: format(date.pickup.date, "yyyy-MM-dd HH:mm:ss.SSS"),
        delivery_schedule: format(date.delivery.date, "yyyy-MM-dd HH:mm:ss.SSS"),
        pending: "",
        distance: 10.5,
        note: formattedNote,
        order_pickup_time_slot_date: format(date.pickup.date, "yyyy-MM-dd HH:mm:ss.SSS"),
        order_delivery_time_slot_date: format(date.delivery.date, "yyyy-MM-dd HH:mm:ss.SSS"),
        laundryZoneId: store?.zone_id,
        coupon_code: coupon?.coupon?.code,
        order_pickup_time_slot: `${date.pickup.slot?.start_time} - ${date.pickup.slot?.end_time}`,
        order_delivery_time_slot: `${date.delivery.slot?.start_time} - ${date.delivery.slot?.end_time}`,
        cart: Object.values(reviewItems),
        tax_amount: tax,
        order_amount: totalPrice,
        booking_type: normalizedBookingType,
        ...(sameDayOrder ? { same_day_token: sameDayToken } : {}),
      },
    ];

    const serializedPayload = JSON.stringify(payloads);
    if (orderSubmissionRef.current?.payload !== serializedPayload) {
      orderSubmissionRef.current = {
        payload: serializedPayload,
        key: crypto.randomUUID(),
      };
    }

    storeOrderCreate(payloads, orderSubmissionRef.current.key)
      .then((res) => afterOrder(res, payloads[0].booking_type))
      .catch((er) => {
        setSubmitting(false);
        const errorData = er?.response?.data;
        const sameDayErrorCode =
          errorData?.code ||
          errorData?.errors?.code ||
          errorData?.errors?.[0]?.code;
        const msg = sameDayOrder && sameDayErrorCode
          ? getSameDayErrorMessage(sameDayErrorCode)
          : errorData?.errors?.message || er?.message || "Something went wrong";
        alert(msg);
        if (sameDayOrder && sameDayErrorCode) {
          toast.error(msg);
        } else if (er?.response?.data?.errors?.length) {
          er.response.data.errors.forEach((x) => toast.error(x.message));
        }
      });
  };

  /* Seal functionality disabled:
  const masterSealRef = React.useRef(null);
  const inputRefs = React.useRef([]);
  const norm = (s) => (s || "").trim();
  const master = norm(sealNo);
  const masterErr = master && master.length < 6 ? "Seal must be at least 6 characters" : "";
  const errs = {};
  const seen = new Set();
  childSeal.forEach((v, i) => {
    const x = norm(v);
    if (!x) return;
    if (x.length < 6) { errs[i] = "Seal must be at least 6 characters"; return; }
    if (seen.has(x)) errs[i] = "Duplicate child seal";
    else seen.add(x);
    if (master && x === master) errs[i] = "Same as master seal not allowed";
  });
  const lastEmpty = childSeal.length && !norm(childSeal[childSeal.length - 1]);
  const isSealInvalid = (master && master.length < 6) || Object.keys(errs).length > 0;
  */

  // ==================== STEP LOCK HELPERS ====================
  const isStepComplete = (s) => {
    if (s === 1) return !!selectedCustomer;
    if (s === 2) return !!orderTypeId;
    if (s === 3) return cart.length > 0;
    if (s === 4) {
      if (!date?.pickup?.date || !date?.delivery?.date || !address?.pickup_address) return false;
      if (isSameDay && (!sameDayToken || !date.pickup.slot || !date.delivery.slot)) return false;
      if (isSameDay && format(date.pickup.date, "yyyy-MM-dd") !== format(date.delivery.date, "yyyy-MM-dd")) return false;
      if (selfPick === "true" || selfPick === true) return !!address?.destination_address;
      return true;
    }
    return false;
  };

  const stepsRef = React.useRef(null);

  const stepClass = (s) => {
    if (isStepComplete(s)) return "step-completed";
    if (s === step) return "step-active";
    return "step-disabled";
  };

  const scrollToSteps = () => {
    setTimeout(() => stepsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  // ==================== RENDER ====================
  return (
    <div className="container py-4" style={{ minHeight: "80vh" }}>
      {/* Header */}
      <div className="row mb-3">
        <div className="col-12">
          <h3 className="fw-bold mb-0">New Order</h3>
        </div>
      </div>

      {/* Stepper */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {stepLabels.map((label, i) => {
              const idx = i + 1;
              const done = isStepComplete(idx);
              const active = idx === step;
              const disabled = !done && !active;
              return (
                <React.Fragment key={label}>
                  {i > 0 && <div className="text-muted" style={{ fontSize: "12px" }}>›</div>}
                  <div
                    className={`d-flex align-items-center gap-1 px-2 py-1 rounded ${
                      done ? "bg-success text-white" : active ? "bg-primary text-white" : "bg-light text-muted"
                    }`}
                    style={{
                      fontSize: "13px",
                      cursor: done ? "pointer" : "default",
                      opacity: disabled ? 0.5 : 1,
                    }}
                    onClick={() => done && setStep(idx)}
                  >
                    {done ? "✓" : idx}
                    <span className="d-none d-sm-inline">{label}</span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* ==================== STEPS CONTENT ==================== */}
      <div className="row" ref={stepsRef}>
        {/* Main content column (full width for steps 1-2, 8 cols for 3-5) */}
        <div className={step >= 3 ? "col-lg-8" : "col-12"}>
          {/* ===== STEP 1: CUSTOMER ===== */}
          <StepCard
            title="1. Select Customer"
            step={1}
            currentStep={step}
            completed={isStepComplete(1)}
            onEdit={() => setStep(1)}
          >
            {selectedCustomer ? (
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>{selectedCustomer.f_name} {selectedCustomer.l_name}</strong>
                  <br />
                  <small className="text-muted">{selectedCustomer.phone} • {selectedCustomer.email}</small>
                  {referralPercent > 0 && (
                    <div className="referral-blink mt-1">
                      🎉 You've received <strong>{referralPercent}%</strong> referral discount from{" "}
                      <strong>{referrerFirstName} {referrerLastName}</strong>{" "}
                      on your next order!
                    </div>
                  )}
                </div>
                <button className="btn btn-sm btn-outline-secondary" onClick={resetCustomer}>
                  Change
                </button>
              </div>
            ) : (
              <>
                <div className="position-relative mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name or phone (3XXXXXXXX)"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      doCustomerSearch(e.target.value);
                    }}
                    style={{ paddingLeft: "40px", height: "44px" }}
                  />
                  <BsSearch style={{ position: "absolute", top: "50%", left: "14px", transform: "translateY(-50%)", color: "#999" }} />
                </div>
                {customerLoading && <div className="text-center py-2"><Spinner size="sm" variant="primary" /></div>}
                {customerResults.length > 0 && (
                  <div className="list-group" style={{ maxHeight: "260px", overflowY: "auto" }}>
                    {customerResults.map((c) => (
                      <button
                        key={c.id}
                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                        onClick={() => selectCustomer(c)}
                      >
                        <div>
                          <strong>{c.f_name} {c.l_name}</strong>
                          <br />
                          <small className="text-muted">{c.phone} • {c.email}</small>
                        </div>
                        <small className="text-muted">{c.laundry_orders_count || 0} orders</small>
                      </button>
                    ))}
                  </div>
                )}
                {searchTerm && !customerLoading && customerResults.length === 0 && (
                  <p className="text-muted text-center mb-0">No customers found</p>
                )}
              </>
            )}
          </StepCard>

          {/* ===== STEP 2: ORDER TYPE ===== */}
          <StepCard
            title="2. Choose Order Type"
            step={2}
            currentStep={step}
            completed={isStepComplete(2)}
            onEdit={() => setStep(2)}
          >
            <div className="d-flex gap-3 mb-3 flex-wrap">
              {["hub", "store", "come & collect", "bespoke service"].map((val) => (
                <button
                  key={val}
                  className={`btn ${alreadySelectedProcessing === val || alreadySelectedOrderType === val || (val === "bespoke service" && (showBespokeTypes || orderTypeId > 4)) ? "btn-dark text-white" : "btn-outline-dark"}`}
                  onClick={() => {
                    if (val === "hub" || val === "store") {
                      selectProcessing(val);
                      setShowBespokeTypes(false);
                      setBookingRadio(false);
                    } else if (val === "come & collect") {
                      navigate("/store/orders/book-an-appointment");
                    } else {
                      setShowBespokeTypes(true);
                      setBookingRadio(false);
                    }
                  }}
                >
                  {val === "hub" ? "Hub Order" : val === "store" ? "Store Order" : val === "come & collect" ? "Come & Collect" : "Bespoke Service"}
                </button>
              ))}
            </div>
            {alreadySelectedProcessing && !showBespokeTypes && (
              <>
                <hr />
                <h6 className="fw-semibold mb-3">Select Delivery Type</h6>
                <div className="d-flex gap-3 flex-wrap">
                  {[
                    { id: 1, title: "Regular", desc: "Delivery in 2 Days" },
                    { id: 2, title: "Express", desc: "Delivery in 24 Hours" },
                  ].map((opt) => (
                    <div
                      key={opt.id}
                      className={`p-3 border rounded flex-fill text-center ${orderTypeId === opt.id ? "border-primary bg-light" : ""}`}
                      style={{ cursor: "pointer", minWidth: "160px" }}
                      onClick={() => selectBooking(opt.id, opt.title)}
                    >
                      <strong>{opt.title}</strong>
                      {opt.desc && <small className="d-block text-muted">{opt.desc}</small>}
                    </div>
                  ))}
                </div>
              </>
            )}
            {showBespokeTypes && (
              <>
                <hr />
                <h6 className="fw-semibold mb-3">Select Bespoke Service</h6>
                <div className="d-flex gap-3 flex-wrap">
                  {[...dts].filter((t) => t.id != 1 && t.id != 2 && t.id != 3).sort((a, b) => a.id - b.id).map((opt) => (
                    <div
                      key={opt.id}
                      className={`p-3 border rounded flex-fill text-center ${orderTypeId === opt.id ? "border-primary bg-light" : ""}`}
                      style={{ cursor: "pointer", minWidth: "160px" }}
                      onClick={() => selectBooking(opt.id, opt.title)}
                    >
                      <strong>{opt.title}</strong>
                      {opt.description && <small className="d-block text-muted">{opt.description}</small>}
                    </div>
                  ))}
                </div>
              </>
            )}
            {bookingRadio && (
              <>
                <hr />
                <h6 className="fw-semibold mb-3">Select Booking Type</h6>
                <div className="d-flex gap-3">
                  {[{ id: 1, label: "Regular", desc: "Delivery in 2 Days" }, { id: 2, label: "Express", desc: "Delivery in 24 Hours" }].map((opt) => (
                    <div
                      key={opt.id}
                      className={`p-3 border rounded flex-fill text-center ${bookingType == opt.id ? "border-primary bg-light" : ""}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => selectBookingType(opt.id)}
                    >
                      <strong>{opt.label}</strong>
                      <small className="d-block text-muted">{opt.desc}</small>
                    </div>
                  ))}
                </div>
              </>
            )}
          </StepCard>

          {/* ===== STEP 3: ADD ITEMS ===== */}
          <StepCard
            title={`3. Add Items ${cart.length > 0 ? `(${total} items)` : ""}`}
            step={3}
            currentStep={step}
            completed={isStepComplete(3)}
            onEdit={() => setStep(3)}
            wide
          >
            {initLoading ? (
              <div className="text-center py-4"><Spinner variant="primary" /></div>
            ) : (
              <>
              <div className="service-picker-fix">
                <ServicePicker payload={payload} selectService={selectService} services={services} />
              </div>
                <div className="text-center mb-3">
                  <span className="fw-bold" style={{ fontSize: "16px", color: "#656565" }}>2. Select Category</span>
                </div>
                <div className="d-flex gap-2 px-3 mb-3 overflow-auto justify-content-center" style={{ flexWrap: "nowrap" }}>
                  {categories.map((cat) => (
                    <div
                      key={cat.id}
                      className={`px-3 py-2 rounded-pill ${cat.id === payload.categoryId ? "bg-dark text-white" : "bg-light"}`}
                      style={{ cursor: "pointer", whiteSpace: "nowrap", fontSize: "14px" }}
                      onClick={() => selectCategory(cat.id)}
                    >
                      {cat.name}
                    </div>
                  ))}
                </div>

                <div className="row justify-content-center mb-3">
                  <div className="col-lg-8 col-12">
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search items..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ paddingLeft: "40px", height: "44px", borderRadius: "10px" }}
                      />
                      <BsSearch style={{ position: "absolute", top: "50%", left: "14px", transform: "translateY(-50%)", color: "#999" }} />
                      {search && (
                        <BsXCircle
                          style={{ position: "absolute", top: "50%", right: "14px", transform: "translateY(-50%)", cursor: "pointer", color: "#999" }}
                          onClick={() => setSearch("")}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="row px-2 laundry-items-fix">
                  {itemsLoading ? (
                    <div className="col-12 text-center py-4"><Spinner variant="warning" /></div>
                  ) : fetchError ? (
                    <div className="col-12 text-center py-4">
                      <p className="text-danger">Failed to load items.</p>
                      <button className="btn btn-warning" onClick={() => setReload(Date.now().toString())}>Retry</button>
                    </div>
                  ) : filteredItems.length === 0 ? (
                    <div className="col-12 text-center py-4 text-muted">
                      {search ? "No matching items" : "No items in this category"}
                    </div>
                  ) : (
      <div className="row">
                      {filteredItems.map(({ item: e, i: originalIndex }) => (
                        <div className="col-lg-6 col-12 mb-3" key={`${e.laundry_item_id}-${e.serviceId}`}>
                          <LaundryItemCard
                            laundryItem={getLaundryItem(e.laundry_item_id)}
                            description={getLaundryItem(e.laundry_item_id)?.description}
                            campaign={campaign}
                            item={e}
                            itemIndex={originalIndex}
                            changeAddonQuantity={changeAddonQuantity}
                            changeItemQuantity={changeItemQuantity}
                            toggleAddons={toggleAddons}
                            offer={getOffer(e.laundry_item_id)}
                            bundleMessage={getBundleMessage(e)}
                            onNoteChange={(note) => changeItemNote(originalIndex, note)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Total bar */}
                <div className="d-flex justify-content-between align-items-center px-3 py-3 bg-light rounded mt-2">
                  <span className="fw-semibold">Total: {total} item{total !== 1 ? "s" : ""}</span>
                  <span className="fw-bold text-primary fs-5">Rs. {amount?.toLocaleString()}</span>
                </div>
                {total > 0 && (
                  <div className="d-flex justify-content-end mt-2">
                    <button className="btn btn-sm btn-outline-danger" onClick={() => setCShow(true)}>
                      Clear Cart
                    </button>
                  </div>
                )}
              </>
            )}
          </StepCard>

          {/* ===== STEP 4: SUMMARY ===== */}
          <StepCard
            title="4. Summary"
            step={4}
            currentStep={step}
            completed={isStepComplete(4)}
            onEdit={() => setStep(4)}
          >
            {/* Schedule Dates */}
            <div className="mb-3">
              <h6 className="fw-semibold mb-2">
                Schedule Dates {isSameDay && <span className="text-primary">(Same Day)</span>}
              </h6>
              {datesLoading ? (
                <div className="text-center py-2"><Spinner size="sm" variant="primary" /></div>
              ) : (
                <div className="d-flex gap-4 flex-wrap">
                  <div
                    className="p-3 border rounded flex-fill"
                    style={{ cursor: "pointer", minWidth: "180px" }}
                    onClick={() => step === 4 && setShowDatePicker("pickup")}
                  >
                    <small className="text-muted">Pickup</small>
                    <div className="fw-semibold">
                      {date.pickup?.date ? format(date.pickup.date, "eee, do MMM") : "Select"}
                      {date.pickup?.slot && <span className="d-block" style={{ fontSize: "12px" }}>{date.pickup.slot.start_time} - {date.pickup.slot.end_time}</span>}
                    </div>
                  </div>
                  <div
                    className="p-3 border rounded flex-fill"
                    style={{ cursor: "pointer", minWidth: "180px" }}
                    onClick={() => step === 4 && date.pickup?.date && setShowDatePicker("delivery")}
                  >
                    <small className="text-muted">Delivery</small>
                    <div className="fw-semibold">
                      {date.delivery?.date ? format(date.delivery.date, "eee, do MMM") : "Select"}
                      {date.delivery?.slot && <span className="d-block" style={{ fontSize: "12px" }}>{date.delivery.slot.start_time} - {date.delivery.slot.end_time}</span>}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Address */}
            <div className="mb-3">
              <h6 className="fw-semibold mb-2">Address</h6>
              <div className="p-3 border rounded">
                <div className="d-flex justify-content-between align-items-start">
                  <div style={{ flex: 1 }}>
                    <small className="text-muted">Pickup</small>
                    <p className="mb-1">
                      {address?.pickup_address
                        ? `${address.pickup_address.addressLine1}, ${address.pickup_address.addressLine2}, ${address.pickup_address.area}, ${address.pickup_address.area_phase}`
                        : "Store address"}
                    </p>
                    <small className="text-muted">Delivery</small>
                    <div
                      style={{ cursor: (selfPick === "true" || selfPick === true) ? "pointer" : "default" }}
                      onClick={() => {
                        if ((selfPick === "true" || selfPick === true) && step === 4)
                          setAddressModal("delivery");
                      }}
                    >
                      <p className="mb-0">
                        {address?.destination_address
                          ? `${address.destination_address.addressLine1}, ${address.destination_address.addressLine2}, ${address.destination_address.area}, ${address.destination_address.area_phase}`
                          : "Select address"}
                      </p>
                    </div>
                  </div>
                  {(selfPick === "true" || selfPick === true) && (
                    <button className="btn btn-sm btn-outline-primary" onClick={() => step === 4 && setAddressModal("delivery")}>
                      {address?.destination_address ? "Change" : "Add"}
                    </button>
                  )}
                </div>
                <div className="form-check mt-2">
                  <input
                    className="form-check-input" type="checkbox" id="self-pick"
                    checked={selfPick === "false" || selfPick === false}
                    onChange={(e) => dispatch({ type: "setSelfPick", payload: !e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="self-pick" style={{ fontSize: "13px" }}>
                    Customer will collect delivery from store
                  </label>
                </div>
              </div>
            </div>

            {/* Coupon */}
            <div className="mb-3">
              <h6 className="fw-semibold mb-2">Apply Coupon</h6>
              <div className="input-group" style={{ height: "44px" }}>
                <input
                  type="text" className="form-control" placeholder="Enter Coupon Code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <button
                  className={`btn text-white fw-semibold ${coupon ? "btn-danger" : "btn-primary"}`}
                  onClick={checkCoupon} disabled={cLoading}
                >
                  {cLoading ? <Spinner size="sm" /> : coupon ? "Remove" : "Apply"}
                </button>
              </div>
            </div>

            {/* Notes */}
            <div className="card mb-3">
              <div className="card-body">
                <OrderNotesPicker
                  apiNotes={apiNotes}
                  value={note}
                  onChange={(e) => dispatch({ type: "setNote", payload: e })}
                />
              </div>
            </div>
            {/* Note display in review */}
            {note && note !== "" ? (
              <div className="card mb-3">
                <div className="card-body">
                  <h5 className="fw-bold mb-3">Order Note / Instructions.</h5>
                  <ul>
                    {(note || []).map((n, i) => <li key={n.id ?? i}>{n.note}</li>)}
                  </ul>
                </div>
              </div>
            ) : null}
          </StepCard>

          {/* ===== STEP 5: REVIEW & PLACE ===== */}
          <StepCard
            title="5. Review & Place Order"
            step={5}
            currentStep={step}
            completed={false}
            onEdit={() => setStep(5)}
          >
            {/* Items (accordion) */}
            {groupedCart.map((group) => (
              <div className="card mb-2" key={group.services_id}>
                <div className="card-body py-2">
                  <div
                    className="d-flex justify-content-between align-items-center cursor-pointer"
                    onClick={() => setOpen((p) => ({ ...p, [group.services_id]: !p[group.services_id] }))}
                  >
                    <span className="fw-semibold">{group.service}</span>
                    <i className={`fa-solid fa-angle-${open[group.services_id] ? "up" : "down"}`}></i>
                  </div>
                  <Collapse isOpened={open[group.services_id]}>
                    <div className="mt-2">
                      {group.items.map((y, j) => (
                        <div key={j} className="d-flex justify-content-between py-1" style={{ fontSize: "13px" }}>
                          <span>
                            {y.quantity} x {y.name}
                            {y.free_items > 0 && <span className="text-success ms-1">({y.free_items} free)</span>}
                            {y.item_note && <span className="d-block text-muted" style={{ fontSize: "11px" }}>Note: {y.item_note}</span>}
                          </span>
                          <span>Rs. {((y.quantity - (y.free_items || 0)) * y.price).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </Collapse>
                </div>
              </div>
            ))}

            {/* Pricing */}
            {deliveryTypes && (
              <div className="card mb-3">
                <div className="card-body py-2">
                  <div className="d-flex justify-content-between py-1"><span>Total Items</span><span>{total} item(s)</span></div>
                  <div className="d-flex justify-content-between py-1"><span>Item Price</span><span>Rs. {amount?.toLocaleString()}</span></div>
                  {referralDiscount > 0 && (
                    <div className="d-flex justify-content-between py-1" style={{ backgroundColor: "#d0f0c0" }}>
                      <span>Referral Discount</span><span>- Rs. {Math.round(referralDiscount)?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between py-1"><span>Subtotal</span><span>Rs. {Math.round(subtotal)?.toLocaleString()}</span></div>
                  {discount > 0 && (
                    <div className="d-flex justify-content-between py-1" style={{ backgroundColor: "#94e19459" }}>
                      <span>Discount {coupon?.coupon?.code && `(${coupon.coupon.code})`}</span><span>-Rs. {Math.round(discount)?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between py-1"><span>VAT / Tax</span><span>Rs. {Math.round(tax)?.toLocaleString()}</span></div>
                  <div className="d-flex justify-content-between py-1"><span>Delivery Fee</span><span>Rs. {Math.round(deliveryCharge || 0)?.toLocaleString()}</span></div>
                  <hr className="my-1" />
                  <div className="d-flex justify-content-between py-1 fw-bold"><span>Total</span><span>Rs. {Math.round(totalPrice)?.toLocaleString()}</span></div>
                </div>
              </div>
            )}

            {/* Schedule */}
            <div className="d-flex gap-3 mb-3 flex-wrap">
              <div className="p-2 border rounded flex-fill" style={{ fontSize: "13px" }}>
                <small className="text-muted">Pickup</small>
                <div className="fw-semibold">{date.pickup?.date ? format(date.pickup.date, "eee, do MMM") : "—"}</div>
                <small>{date.pickup?.slot ? `${date.pickup.slot.start_time} - ${date.pickup.slot.end_time}` : "—"}</small>
              </div>
              <div className="p-2 border rounded flex-fill" style={{ fontSize: "13px" }}>
                <small className="text-muted">Delivery</small>
                <div className="fw-semibold">{date.delivery?.date ? format(date.delivery.date, "eee, do MMM") : "—"}</div>
                <small>{date.delivery?.slot ? `${date.delivery.slot.start_time} - ${date.delivery.slot.end_time}` : "—"}</small>
              </div>
            </div>

            {/* Address */}
            <p style={{ fontSize: "13px" }} className="mb-3">
              <strong>Pickup:</strong> {address?.pickup_address ? `${address.pickup_address.addressLine1}, ${address.pickup_address.area}` : "Store"}<br />
              <strong>Delivery:</strong> {address?.destination_address ? `${address.destination_address.addressLine1}, ${address.destination_address.area}` : "Same as pickup"}
            </p>
            <hr />

            {/* Payment Method */}
            <div className="mb-3">
              <h6 className="fw-semibold mb-2">Payment Method</h6>
              <div className="d-flex gap-2 flex-wrap">
                {[
                  { key: "cash_on_delivery", label: "Cash On Delivery", img: "cash-on-delivery.png" },
                  { key: "card_on_delivery", label: "Card on Delivery", img: "card-on-delivery.png" },
                  { key: "up_front_cash_payment", label: "Upfront Cash Payment", img: "card-on-delivery.png" },
                ].map((pm) => (
                  <div
                    key={pm.key}
                    className={`p-2 border rounded d-flex align-items-center gap-2 ${paymentMethod === pm.key ? "border-primary bg-light" : ""}`}
                    style={{ cursor: "pointer", fontSize: "13px" }}
                    onClick={() => setPaymentMethod(pm.key)}
                  >
                    <img src={`/assets/images/${pm.img}`} alt="" style={{ height: "32px", width: "32px", objectFit: "contain" }} />
                    <span>{pm.label}</span>
                  </div>
                ))}
                <button
                  type="button"
                  disabled={!canPayWithWallet}
                  className={`p-2 border rounded d-flex align-items-center gap-2 ${paymentMethod === "wallet_on_delivery" ? "border-primary bg-light" : "bg-white"}`}
                  style={{ cursor: canPayWithWallet ? "pointer" : "not-allowed", fontSize: "13px", opacity: canPayWithWallet ? 1 : 0.5 }}
                  onClick={() => setPaymentMethod("wallet_on_delivery")}
                >
                  <img src="/assets/images/card-on-delivery.png" alt="" style={{ height: "32px", width: "32px", objectFit: "contain" }} />
                  <span>Wallet (Points. {walletBalance.toLocaleString()})</span>
                </button>
              </div>
            </div>

            {/* Seal functionality disabled:
            <div className="mb-3">
              <h6 className="fw-semibold mb-2">Seal No.</h6>
              <div className="input-group">
                <input type="text" className="form-control" value={sealNo} ref={masterSealRef}
                  onChange={(e) => setSealNo(e.target.value)} placeholder="Master seal" />
                <span className="input-group-text" role="button" onClick={() => masterSealRef.current?.focus()}><LuScanLine /></span>
              </div>
              {masterErr && <small className="text-danger">{masterErr}</small>}
              {childSeal.map((val, idx) => (
                <div key={idx} className="d-flex align-items-center gap-2 mt-2">
                  <div className="input-group">
                    <input type="text" className={`form-control ${errs[idx] ? "is-invalid" : ""}`}
                      ref={(el) => (inputRefs.current[idx] = el)} value={val}
                      onChange={(e) => setChildSeal((p) => p.map((x, i) => (i === idx ? e.target.value : x)))}
                      placeholder={`Child seal ${idx + 1}`} />
                    <span className="input-group-text" role="button" onClick={() => inputRefs.current[idx]?.focus()}><LuScanLine /></span>
                    <span className="input-group-text" role="button" onClick={() => setChildSeal((p) => p.filter((_, i) => i !== idx))}><CiSquareMinus /></span>
                  </div>
                  {errs[idx] && <small className="text-danger">{errs[idx]}</small>}
                </div>
              ))}
              <div className="mt-2 d-flex align-items-center gap-2">
                <span className="fw-semibold" style={{ fontSize: "13px" }}>Add Child Seal</span>
                <span role="button" onClick={() => setChildSeal((p) => [...p, ""])}><BsPlusSquare /></span>
              </div>
            </div>
            */}
          </StepCard>
        </div>

        {/* ===== RIGHT SIDEBAR: Cart Preview (steps 3-5) ===== */}
        {step >= 3 && (
          <div className="col-lg-4">
            <div className="sticky-top" style={{ top: "20px" }}>
            <div className="card">
              <div className="card-body">
                <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                  Cart
                  {cart.length > 0 && <span className="badge bg-primary">{total}</span>}
                </h5>
                <hr />
                {cart.length === 0 ? (
                  <p className="text-muted text-center mb-0">Cart is empty</p>
                ) : (
                  <>
                    <div style={{ maxHeight: "30vh", overflowY: "auto" }}>
                      {groupedCart.map((group) => (
                        <div key={group.services_id} className="mb-2">
                          <div
                            className="d-flex justify-content-between align-items-center cursor-pointer"
                            onClick={() => setOpen((p) => ({ ...p, [`preview_${group.services_id}`]: !p[`preview_${group.services_id}`] }))}
                          >
                            <strong style={{ fontSize: "13px" }}>{group.service}</strong>
                            <i className={`fa-solid fa-angle-${open[`preview_${group.services_id}`] ? "up" : "down"}`} style={{ fontSize: "12px" }}></i>
                          </div>
                          <Collapse isOpened={open[`preview_${group.services_id}`]}>
                            {group.items.map((item, i) => (
                              <div key={i} className="d-flex justify-content-between align-items-start ps-2 py-1 border-bottom" style={{ fontSize: "12px" }}>
                                <div>
                                  <span className="fw-medium">{item.name}</span>
                                  <span className="text-muted d-block">x{item.quantity}{item.free_items > 0 && <span className="text-success"> ({item.free_items} free)</span>}</span>
                                  {item.addons?.map((ad) => (
                                    <small key={ad.id} className="d-block text-muted">+ {ad.name} x{ad.qty}</small>
                                  ))}
                                </div>
                                <span className="fw-medium">Rs. {((item.quantity - (item.free_items || 0)) * item.price + item.addons.reduce((s, a) => s + a.qty * a.price, 0)).toLocaleString()}</span>
                              </div>
                            ))}
                          </Collapse>
                        </div>
                      ))}
                    </div>
                    <hr />
                    {/* Full pricing breakdown */}
                    <div style={{ fontSize: "13px" }}>
                      <div className="d-flex justify-content-between py-1"><span>Total Items</span><span>{total} item(s)</span></div>
                      <div className="d-flex justify-content-between py-1"><span>Item Price</span><span>Rs. {amount?.toLocaleString()}</span></div>
                      {referralDiscount > 0 && (
                        <div className="d-flex justify-content-between py-1" style={{ backgroundColor: "#d0f0c0" }}>
                          <span>Referral Discount</span><span>- Rs. {Math.round(referralDiscount)?.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="d-flex justify-content-between py-1"><span>Subtotal</span><span>Rs. {Math.round(subtotal)?.toLocaleString()}</span></div>
                      {discount > 0 && (
                        <div className="d-flex justify-content-between py-1" style={{ backgroundColor: "#94e19459" }}>
                          <span>Discount {coupon?.coupon?.code && `(${coupon.coupon.code})`}</span><span>-Rs. {Math.round(discount)?.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="d-flex justify-content-between py-1"><span>VAT / Tax</span><span>Rs. {Math.round(tax)?.toLocaleString()}</span></div>
                      <div className="d-flex justify-content-between py-1"><span>Delivery Fee</span><span>Rs. {Math.round(deliveryCharge || 0)?.toLocaleString()}</span></div>
                      <hr className="my-1" />
                      <div className="d-flex justify-content-between py-1 fw-bold"><span>Total</span><span>Rs. {Math.round(totalPrice)?.toLocaleString()}</span></div>
                    </div>

                  </>
                )}
              </div>
            </div>

            {/* Navigation buttons */}
            <div className="d-flex flex-column gap-2 mt-3">
              {step > 1 && step <= 5 && (
                <button className="btn btn-outline-secondary" onClick={() => setStep((s) => s - 1)}>
                  ← Back
                </button>
              )}
              {step === 3 && cart.length > 0 && (
                <button className="btn btn-dark text-white fw-semibold" onClick={() => { scrollToSteps(); setStep(4); }}>
                  Proceed to Summary →
                </button>
              )}
              {step === 4 && isStepComplete(4) && (
                <button className="btn btn-dark text-white fw-semibold" onClick={() => { scrollToSteps(); setStep(5); }}>
                  Proceed to Review →
                </button>
              )}
              {step === 5 && (
                <button
                  className="btn btn-primary text-white fw-semibold"
                  disabled={submitting /* || isSealInvalid || lastEmpty */}
                  onClick={() => {
                    // Seal validation disabled:
                    // if (!sealNo || sealNo === "") {
                    //   toast.error("Please add seal no. to continue.");
                    //   return;
                    // } else handlePlaceOrder();
                    handlePlaceOrder();
                  }}
                >
                  {submitting ? <Spinner size="sm" className="me-1" /> : null}
                  Place Order
                </button>
              )}
            </div>
            </div>
          </div>
        )}
      </div>

      {/* Clear Cart Confirmation */}
      <Modal show={cShow} onHide={() => setCShow(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="text-primary">Warning...</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="fw-normal text-center">Are you sure you want to clear your cart?</h5>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-between">
          <button className="btn btn-outline-secondary" onClick={() => setCShow(false)}>Cancel</button>
          <button className="btn btn-dark text-white fw-semibold" onClick={() => { setCShow(false); cCart(); }}>Clear</button>
        </Modal.Footer>
      </Modal>

      {/* Warning modal for changing service (non-1/2 types) */}
      <Modal show={Boolean(tempServ)} onHide={() => setTempServ(null)} backdrop="static" keyboard={false} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="text-primary">Warning...</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="fw-normal text-center">
            You already have {total} item(s) in your cart. If you change service your cart will be cleared.
          </h5>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-between">
          <button className="btn btn-outline-secondary" onClick={() => setTempServ(null)}>Cancel</button>
          <button className="btn btn-dark text-white fw-semibold" onClick={() => cCart(tempServ, true)}>
            Change Service
          </button>
        </Modal.Footer>
      </Modal>

      {/* Date Picker Modals */}
      <Modal show={Boolean(showDatePicker)} centered onHide={() => setShowDatePicker(null)} backdrop="static">
        <Modal.Header closeButton className="py-2">
          <Modal.Title style={{ fontSize: "18px" }}>
            Choose {showDatePicker === "pickup" ? "Pickup" : "Delivery"} Date / Time
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showDatePicker && (
            <DatePicker
              range={showDatePicker === "pickup" ? pickupRange : deliveryRange}
              selected={date[showDatePicker]?.date}
              onChange={HandleDateChange}
              orderType={bookingType}
              timeSlot={date[showDatePicker]?.slot}
              pickup={showDatePicker === "delivery" ? { date: date.pickup?.date ? format(date.pickup.date, "yyyy-MM-dd") : null, slotId: date.pickup?.slot?.id } : {}}
              type={showDatePicker === "pickup" ? 1 : 2}
              deliveryTypeId={isSameDay ? sameDayDeliveryTypeId : null}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Address Picker Modal */}
      <AddressPicker
        show={Boolean(addressModal)}
        closeButton
        onHide={() => setAddressModal(null)}
        title="Select Delivery Address"
        onSelect={HandleAddressChange}
        showEdit={false}
        showDelete={false}
      />
    </div>
  );
};

// ==================== STEP CARD WRAPPER ====================
const StepCard = ({ title, step, currentStep, completed, onEdit, children, wide }) => {
  const isActive = step === currentStep;
  const isDone = completed;
  const disabled = !isActive && !isDone;

  if (isDone && !isActive) {
    return (
      <div className="card mb-3 border-success">
        <div className="card-body py-2">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="fw-bold mb-0 text-success" style={{ fontSize: "15px" }}>
              ✓ {title}
            </h5>
            <button className="btn btn-sm btn-outline-success" onClick={onEdit}>Edit</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`card mb-3 ${disabled ? "opacity-50" : ""}`} style={{ pointerEvents: disabled ? "none" : "auto" }}>
      <div className="card-body">
        <h5 className="fw-bold mb-3" style={{ fontSize: "16px" }}>{title}</h5>
        {children}
      </div>
    </div>
  );
};
